import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/store/user'

const routes = [
  { path: '/login', name: 'Login', component: () => import('@/views/Login.vue'), meta: { requiresAuth: false } },
  { path: '/register', name: 'Register', component: () => import('@/views/Register.vue'), meta: { requiresAuth: false } },
  {
    path: '/payment/return',
    name: 'PaymentReturn',
    component: () => import('@/views/PaymentReturn.vue'),
    meta: { requiresAuth: true, title: 'Payment Return' }
  },
  {
    path: '/',
    component: () => import('@/components/Layout/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: 'home', name: 'Home', component: () => import('@/views/Home.vue'), meta: { title: 'Home' } },
      { path: '', name: 'Dashboard', component: () => import('@/views/Dashboard.vue') },
      { path: 'brand-promotion', name: 'BrandPromotion', component: () => import('@/views/BrandPromotion.vue') },
      { path: 'ai-agent', name: 'AIAgent', component: () => import('@/views/AIAgent.vue') },
      { path: 'ecommerce', name: 'ECommerce', component: () => import('@/views/ECommerce.vue'), meta: { title: 'E-Commerce' } },
      { path: 'ai-agent/chat/:agentSlug', name: 'AgentChat', component: () => import('@/views/agents/AgentChatDynamic.vue') },
      { path: 'profile', name: 'Profile', component: () => import('@/views/Profile.vue') },
      { path: 'enterprise-settings', name: 'EnterpriseSettings', component: () => import('@/views/EnterpriseSettings.vue') },
      { path: 'account-auth', name: 'AccountAuth', component: () => import('@/views/AccountAuth.vue'), meta: { title: 'Account Auth' } },
      {
        path: 'matrix',
        name: 'Matrix',
        component: () => import('@/views/Matrix.vue'),
        meta: { title: 'Matrix' }
      },
      {
        path: 'matrix/social',
        name: 'MatrixSocial',
        component: () => import('@/views/matrix/MatrixSocial.vue'),
        meta: { title: 'Social Media Matrix' }
      },
      {
        path: 'matrix/international-media',
        name: 'MatrixInternationalMedia',
        component: () => import('@/views/matrix/MatrixInternationalMedia.vue'),
        meta: { title: 'International Media Matrix' }
      },
      {
        path: 'matrix/rpa',
        name: 'MatrixRpa',
        component: () => import('@/views/matrix/MatrixRpa.vue'),
        meta: { title: 'RPA Intelligent Matrix' }
      },
      {
        path: 'matrix/group',
        name: 'MatrixGroup',
        component: () => import('@/views/matrix/MatrixGroup.vue'),
        meta: { title: 'Group Control' }
      },
      {
        path: 'welfare/accounts',
        name: 'WelfareAccounts',
        component: () => import('@/views/welfare/WelfareAccounts.vue'),
        meta: { title: 'Account Distribution' }
      },
      {
        path: 'welfare/drama',
        name: 'WelfareDrama',
        component: () => import('@/views/welfare/WelfareDrama.vue'),
        meta: { title: 'Drama Promotion' }
      },
      {
        path: 'welfare/drama/:id/promote',
        name: 'WelfareDramaPromotion',
        component: () => import('@/views/welfare/WelfareDramaPromotion.vue'),
        meta: { title: 'Drama Promotion Details' }
      },
      {
        path: 'welfare/account-center',
        name: 'WelfareAccountCenter',
        component: () => import('@/views/welfare/WelfareAccountCenter.vue'),
        meta: { title: 'Account Center' }
      },
      { path: 'usage-statistics', name: 'UsageStatistics', component: () => import('@/views/UsageStatistics.vue'), meta: { title: 'Usage Statistics' } },
      { path: 'tasks', name: 'Tasks', component: () => import('@/views/Tasks.vue'), meta: { title: 'Task Management' } },
      { path: 'file-management', name: 'FileManagement', component: () => import('@/views/FileManagement.vue'), meta: { title: 'File Management' } },
      { path: 'knowledge-base', name: 'KnowledgeBase', component: () => import('@/views/KnowledgeBase.vue'), meta: { title: 'Knowledge Base' } },
      {
        path: 'system-settings',
        name: 'SystemSettings',
        component: () => import('@/views/system/SystemSettings.vue'),
        meta: { title: 'System Settings', requiresEnterpriseAdmin: true },
        children: [
          { path: '', name: 'SystemSettingsIndex', redirect: '/system-settings/roles' },
          { path: 'roles', name: 'RoleSettings', component: () => import('@/views/system/RoleSettings.vue'), meta: { title: 'Role Settings' } },
          { path: 'users', name: 'UserSettings', component: () => import('@/views/system/UserSettings.vue'), meta: { title: 'User Settings' } },
          { path: 'departments', name: 'DepartmentSettings', component: () => import('@/views/system/DepartmentSettings.vue'), meta: { title: 'Department Settings', requiresDepartmentAdmin: true } }
        ]
      }
    ]
  }
]

const router = createRouter({ history: createWebHistory(), routes })

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  if (userStore.isLoggedIn && !userStore.userInfo) {
    await userStore.fetchUserInfo().catch(() => {})
  }

  if (to.meta.requiresAuth && !userStore.isLoggedIn) {
    next('/login')
  } else if (to.meta.requiresEnterpriseAdmin && !userStore.canAccessSystemSettings) {
    next('/home')
  } else if (to.meta.requiresDepartmentAdmin) {
    // 部门管理员专属路由：超管 / 企业管理员 / 任一部门的 is_department_manager。
    const info = userStore.userInfo || {}
    const allowed =
      userStore.isSuperuser ||
      userStore.isEnterpriseAdmin ||
      info.is_department_admin ||
      info.isDepartmentAdmin ||
      info.is_department_manager ||
      info.isDepartmentManager
    if (!allowed) {
      next('/home')
    } else {
      next()
    }
  } else if (to.path === '/login' && userStore.isLoggedIn) {
    next('/home')
  } else {
    next()
  }
})

export default router
